import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useUser } from '../lib/auth';
import { Card } from '../components/Card';
import { CountryJoinBanner } from '../components/CountryJoinBanner';
import { FixtureCard } from '../components/FixtureCard';
import { GoldenTicketCard } from '../components/GoldenTicketCard';
import { StatBox } from '../components/StatBox';
import { TournamentMarquee } from '../components/TournamentMarquee';

const EARLY_BIRD_END = '2026-06-01T23:59:00Z';
const BRACKET_LOCK = '2026-06-10T23:59:00Z';

function bracketPricing(now: Date) {
  const earlyEnd = new Date(EARLY_BIRD_END);
  const lock = new Date(BRACKET_LOCK);
  if (now >= lock) return { closed: true, stars: 0 };
  if (now < earlyEnd) return { closed: false, stars: 300, strikethrough: 400, badge: 'Early bird' };
  return { closed: false, stars: 400, strikethrough: undefined, badge: null };
}

export function Home() {
  const user = useUser();
  const today = useQuery({
    queryKey: ['fixtures', 'today'],
    queryFn: () => api.fixturesToday(),
  });
  const myLeagues = useQuery({
    queryKey: ['leagues', 'me'],
    queryFn: () => api.leaguesMe(),
  });

  const pricing = bracketPricing(new Date());
  const hasPremium = !!user.data?.hasBracketPremium;

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <TournamentMarquee />

      <GoldenTicketCard
        hasPremium={hasPremium}
        stars={pricing.stars}
        strikethrough={pricing.strikethrough}
        badge={pricing.badge}
        closed={pricing.closed}
      />

      {/* Form stat row */}
      <div className="flex gap-3">
        <StatBox
          label="Points"
          value={user.data?.totalPoints?.toLocaleString() ?? '—'}
          accent="gold"
        />
        <StatBox
          label="Streak"
          value={user.data?.currentStreak ?? 0}
          accent="pitch"
        />
      </div>

      <CountryJoinBanner />

      {/* Today's fixtures */}
      <section>
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="font-display text-2xl uppercase tracking-wide">Today</h2>
          <Link to="/today" className="font-display-alt text-xs uppercase tracking-widest text-text-dim">
            See all →
          </Link>
        </div>
        {today.isLoading && <SkeletonFixtureRow />}
        {today.data && today.data.fixtures.length === 0 && (
          <Card>
            <div className="text-text-dim text-sm">No matches today — back tomorrow.</div>
          </Card>
        )}
        <div className="space-y-2">
          {today.data?.fixtures.slice(0, 4).map((f) => (
            <FixtureCard key={f.id} fixture={f} />
          ))}
        </div>
      </section>

      {/* My leagues */}
      <section>
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="font-display text-2xl uppercase tracking-wide">Leagues</h2>
          <Link to="/leagues" className="font-display-alt text-xs uppercase tracking-widest text-text-dim">
            All →
          </Link>
        </div>
        {myLeagues.data?.leagues.length === 0 && (
          <Card>
            <div className="text-text-dim text-sm">
              You're not in any leagues yet. Country leagues open at WC kickoff.
            </div>
          </Card>
        )}
        <div className="space-y-2">
          {myLeagues.data?.leagues.slice(0, 3).map(({ league, member }) => (
            <Link
              key={league.id}
              to={`/leagues/${league.slug}`}
              className="block rounded-xl border border-border-base bg-bg-card p-4 active:scale-[0.99]"
            >
              <div className="flex items-center justify-between">
                <span className="font-chunky text-base">{league.name}</span>
                <span className="font-mono text-trophy-gold">{member.totalPoints} pts</span>
              </div>
              <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint mt-1">
                Rank #{member.currentRank ?? '—'}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function SkeletonFixtureRow() {
  return (
    <div className="space-y-2 animate-pulse">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-20 rounded-xl bg-bg-card border border-border-base" />
      ))}
    </div>
  );
}
